import { useState } from "react";

const VerticalMetrics = ({ initialData = [] }) => {
  // Initialize with data or empty array
  const [verticals, setVerticals] = useState(
    initialData.length > 0
      ? initialData
      : [
          {
            name: "HEALTHCARE",
            voc: "",
            vocPrevious: "",
            responseTime: "",
            responseTimePrevious: "",
          },
          {
            name: "I&C",
            voc: "",
            vocPrevious: "",
            responseTime: "",
            responseTimePrevious: "",
          },
          {
            name: "CG&T",
            voc: "",
            vocPrevious: "",
            responseTime: "",
            responseTimePrevious: "",
          },
        ]
  );

  const [editing, setEditing] = useState({});

  const handleEdit = (index, field, value) => {
    const updated = [...verticals];
    updated[index][field] = value;
    setVerticals(updated);
  };

  const formatTime = (minutes) => {
    if (!minutes || minutes === "") return "0:00:00";
    const m = parseFloat(minutes);
    const hours = Math.floor(m / 60);
    const mins = Math.floor(m % 60);
    const secs = Math.floor((m % 1) * 60);
    return `${hours}:${String(mins).padStart(2, "0")}:${String(secs).padStart(
      2,
      "0"
    )}`;
  };

  const calculateDelta = (current, previous) => {
    const curr = parseFloat(current) || 0;
    const prev = parseFloat(previous) || 0;
    return curr - prev;
  };

  const getDeltaIcon = (delta, isVoc = false) => {
    if (delta > 0)
      return {
        icon: "▲",
        color: isVoc ? "text-green-600" : "text-red-600",
      };
    if (delta < 0)
      return {
        icon: "▼",
        color: isVoc ? "text-red-600" : "text-green-600",
      };
    return { icon: "►", color: "text-amber-500" };
  };

  const startEdit = (index, field) => {
    setEditing({ [`${index}-${field}`]: true });
  };

  const endEdit = (index, field) => {
    setEditing({ [`${index}-${field}`]: false });
  };

  const isEditing = (index, field) => editing[`${index}-${field}`];

  return (
    <div className="flex gap-8 p-0 w-[70%] mx-auto">
      {/* VOC Section */}
      <div className="flex-1">
        {/* Header */}
        <div className="text-center mb-5 text-xs font-bold text-gray-500 tracking-wider">
          VOC
        </div>

        <div className="flex flex-col gap-3">
          {verticals.map((vertical, index) => {
            const vocDelta = calculateDelta(vertical.voc, vertical.vocPrevious);
            const vocIcon = getDeltaIcon(vocDelta, true);

            return (
              <div
                key={`voc-${index}`}
                className="bg-gradient-to-br from-white to-slate-50 rounded-xl p-4 px-5 shadow-md border border-gray-200 transition-all"
              >
                {/* Top Row: Vertical Name + Current Value */}
                <div className="flex justify-between items-center mb-2">
                  {/* Vertical Name */}
                  <div
                    className="text-sm font-bold"
                    style={{ color: "#144677" }}
                  >
                    {vertical.name}
                  </div>

                  {/* Current VOC - Editable */}
                  {isEditing(index, "voc") ? (
                    <input
                      autoFocus
                      type="number"
                      step="0.1"
                      value={vertical.voc}
                      onChange={(e) => handleEdit(index, "voc", e.target.value)}
                      onBlur={() => endEdit(index, "voc")}
                      className="text-3xl font-extrabold border-none outline-none bg-transparent w-32 text-right p-0"
                      style={{ color: "#144677" }}
                    />
                  ) : (
                    <div
                      onClick={() => startEdit(index, "voc")}
                      className={`text-3xl font-extrabold cursor-pointer ${
                        vertical.voc === "" ? "text-gray-300" : ""
                      }`}
                      style={{
                        color: vertical.voc === "" ? undefined : "#144677",
                      }}
                    >
                      {vertical.voc === "" ? "0.0%" : `${vertical.voc}%`}
                    </div>
                  )}
                </div>

                {/* Bottom Row: Previous Label + Icon + Previous Value */}
                <div className="flex justify-between items-center">
                  {/* Previous Label + Icon */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-gray-400 tracking-wide">
                      PREVIOUS
                    </span>

                    {/* Delta Icon */}
                    <span
                      className={`text-sm flex items-center ${vocIcon.color}`}
                    >
                      {vocIcon.icon}
                    </span>
                  </div>

                  {/* Previous Value - Editable */}
                  {isEditing(index, "vocPrevious") ? (
                    <input
                      autoFocus
                      type="number"
                      step="0.1"
                      value={vertical.vocPrevious}
                      onChange={(e) =>
                        handleEdit(index, "vocPrevious", e.target.value)
                      }
                      onBlur={() => endEdit(index, "vocPrevious")}
                      className="text-base font-semibold text-gray-600 border-none outline-none bg-transparent w-20 text-right p-0"
                    />
                  ) : (
                    <div
                      onClick={() => startEdit(index, "vocPrevious")}
                      className={`text-base font-semibold cursor-pointer ${
                        vertical.vocPrevious === ""
                          ? "text-gray-300"
                          : "text-gray-600"
                      }`}
                    >
                      {vertical.vocPrevious === ""
                        ? "0.0%"
                        : `${vertical.vocPrevious}%`}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* AVG RESPONSE TIME Section */}
      <div className="flex-1">
        {/* Header */}
        <div className="text-center mb-5 text-xs font-bold text-gray-500 tracking-wider">
          AVG RESPONSE TIME
        </div>

        <div className="flex flex-col gap-3">
          {verticals.map((vertical, index) => {
            const timeDelta = calculateDelta(
              vertical.responseTime,
              vertical.responseTimePrevious
            );
            const timeIcon = getDeltaIcon(timeDelta, false);

            return (
              <div
                key={`time-${index}`}
                className="bg-gradient-to-br from-white to-slate-50 rounded-xl p-4 px-5 shadow-md border border-gray-200 transition-all"
              >
                {/* Top Row: Vertical Name + Current Value */}
                <div className="flex justify-between items-center mb-2">
                  {/* Vertical Name */}
                  <div
                    className="text-sm font-bold"
                    style={{ color: "#144677" }}
                  >
                    {vertical.name}
                  </div>

                  {/* Current Response Time - Editable (in minutes) */}
                  {isEditing(index, "responseTime") ? (
                    <input
                      autoFocus
                      type="number"
                      step="0.1"
                      placeholder="minutes"
                      value={vertical.responseTime}
                      onChange={(e) =>
                        handleEdit(index, "responseTime", e.target.value)
                      }
                      onBlur={() => endEdit(index, "responseTime")}
                      className="text-3xl font-extrabold border-none outline-none bg-transparent w-40 text-right p-0"
                      style={{ color: "#144677" }}
                    />
                  ) : (
                    <div
                      onClick={() => startEdit(index, "responseTime")}
                      className={`text-3xl font-extrabold cursor-pointer ${
                        vertical.responseTime === "" ? "text-gray-300" : ""
                      }`}
                      style={{
                        color:
                          vertical.responseTime === "" ? undefined : "#144677",
                      }}
                    >
                      {vertical.responseTime === ""
                        ? "0:00:00"
                        : formatTime(vertical.responseTime)}
                    </div>
                  )}
                </div>

                {/* Bottom Row: Previous Label + Icon + Previous Value */}
                <div className="flex justify-between items-center">
                  {/* Previous Label + Icon */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-gray-400 tracking-wide">
                      PREVIOUS
                    </span>

                    {/* Delta Icon */}
                    <span
                      className={`text-sm flex items-center ${timeIcon.color}`}
                    >
                      {timeIcon.icon}
                    </span>
                  </div>

                  {/* Previous Value - Editable */}
                  {isEditing(index, "responseTimePrevious") ? (
                    <input
                      autoFocus
                      type="number"
                      step="0.1"
                      placeholder="minutes"
                      value={vertical.responseTimePrevious}
                      onChange={(e) =>
                        handleEdit(
                          index,
                          "responseTimePrevious",
                          e.target.value
                        )
                      }
                      onBlur={() => endEdit(index, "responseTimePrevious")}
                      className="text-base font-semibold text-gray-600 border-none outline-none bg-transparent w-24 text-right p-0"
                    />
                  ) : (
                    <div
                      onClick={() => startEdit(index, "responseTimePrevious")}
                      className={`text-base font-semibold cursor-pointer ${
                        vertical.responseTimePrevious === ""
                          ? "text-gray-300"
                          : timeIcon.color
                      }`}
                    >
                      {vertical.responseTimePrevious === ""
                        ? "+ 0.0m"
                        : `${timeDelta > 0 ? "+" : ""}${timeDelta.toFixed(2)}m`}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default VerticalMetrics;
